import { useState, FormEvent } from "react";
import { GradeLevelGroup } from "types/Student";
import { PhoneInput } from "./ui/PhoneInput";
import { TextEdit } from "./ui/TextEdit";
import { DropDown } from "./ui/DropDown";
import { Toast } from "./ui/Toast";
import { useAuth } from "context/AuthContext";
import { useStudents } from "context/StudentsContext";
import { PlanBadge } from "./PlanBadge";
import fetchWithCredentials from "apiClient";

interface AddStudentFormProps {
  preSelectedSubject?: string;
  preSelectedGradeLevel?: string;
  gradeLevelGroups: GradeLevelGroup[];
  onSuccess?: () => void;
  onClose?: () => void;
}

interface FormData {
  name: string;
  phone: string;
  subject: string;
  grade_level: string;
}

export function AddStudentForm({
  preSelectedSubject,
  preSelectedGradeLevel,
  gradeLevelGroups,
  onSuccess,
  onClose,
}: AddStudentFormProps) {
  const { teacherCountry, planLimit } = useAuth();
  const { activeStudentsCount } = useStudents();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    name: "",
    phone: "",
    subject: preSelectedSubject || "",
    grade_level: preSelectedGradeLevel || "",
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.phone) newErrors.phone = "Phone number is required";
    if (!formData.subject) newErrors.subject = "Subject is required";
    if (!formData.grade_level)
      newErrors.grade_level = "Grade level is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetchWithCredentials("/api/teacher/students/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          subject: formData.subject,
          grade_level: formData.grade_level,
        }),
      });

      if (!response) {
        throw new Error("Failed to add student");
      }

      // Reset form
      setFormData({
        name: "",
        phone: "",
        subject: preSelectedSubject || "",
        grade_level: preSelectedGradeLevel || "",
      });
      setErrors({});

      // Notify parent component of success and close modal
      onSuccess?.();
      onClose?.();
    } catch (error: any) {
      setToastMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const gradeLevelOptions = gradeLevelGroups.map((group) => ({
    value: group.gradeLevel,
    label: group.gradeLevel,
  }));

  const subjectOptions = formData.grade_level
    ? gradeLevelGroups
        .find((group) => group.gradeLevel === formData.grade_level)
        ?.subjects.map((subject) => ({
          value: subject,
          label: subject,
        })) || []
    : [];

  return (
    <>
      <form onSubmit={onSubmit} className="space-y-6">
        <TextEdit
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          label="Name"
          placeholder="Enter student's name"
          error={errors.name}
        />

        <div>
          <label
            htmlFor="phone"
            className="block text-xs font-medium text-gray-500 mb-1"
          >
            Phone Number
          </label>
          <PhoneInput
            value={formData.phone}
            onChange={(value) => {
              setFormData((prev) => ({ ...prev, phone: value }));
              if (errors.phone) {
                setErrors((prev) => ({ ...prev, phone: undefined }));
              }
            }}
            error={errors.phone}
            placeholder="Enter phone number"
            defaultCountry={teacherCountry || undefined}
          />
        </div>

        <DropDown
          id="grade_level"
          name="grade_level"
          value={formData.grade_level}
          onChange={handleChange}
          label="Grade Level"
          options={gradeLevelOptions}
          error={errors.grade_level}
          placeholder="Select or type a grade level"
          allowNew={true}
        />

        <DropDown
          id="subject"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          label="Subject"
          options={subjectOptions}
          error={errors.subject}
          disabled={!formData.grade_level}
          placeholder="Select or type a subject"
          allowNew={true}
        />

        <div className="flex justify-end pt-2">
          <div className="relative">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex justify-center rounded-lg border border-transparent bg-blue-600 py-2.5 px-6 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isSubmitting ? "Adding..." : "Add Student"}
            </button>
            <div className="absolute -top-1 -right-1">
              <PlanBadge
                studentCount={activeStudentsCount}
                planLimit={planLimit}
              />
            </div>
          </div>
        </div>
      </form>
      {toastMessage && (
        <Toast
          message={toastMessage}
          type="error"
          onClose={() => setToastMessage(null)}
        />
      )}
    </>
  );
}
